/**
 * KPIOnePagerV2Tab - Orchestrator for the KPI One Pager V2 feature.
 * Connects Redux state to presentational components (ChartConfigurationPanel, OnePagerTable).
 *
 * Data source is controlled by the USE_BACKEND feature flag:
 * - true  → uses useOnePager hook (real API via onePagerApi)
 * - false → uses local dummy data (preserved for QA/development)
 */

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Dimensions, Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import {
	toggleConfigPanel,
	setPendingGrain,
	addGrainSlot,
	removeGrainSlot,
	applyGrainConfig,
	resetGrainConfig,
	setAutoDrillGrains,
	setSort,
	selectGrainSelections,
	selectPendingGrains,
	selectGrainSlotCount,
	selectManualOverride,
	selectSort,
	selectIsConfigExpanded,
} from '../../../../store/slices/onePagerSlice';
import {
	GroupedRow,
	PAGER_COLUMNS,
	DEFAULT_GRAIN_SELECTIONS,
} from '../../constants/onePagerConstants';
import { sortGrainRows } from '../../utils/grainLogic';
import type { NavigableFilterType, OnePagerFilters } from '../../types/onePagerTypes';
import { useOnePager } from '../../hooks/useOnePager';
import { useOnePagerExport, OnePagerExportError } from '../../hooks/useOnePagerExport';
import ChartConfigurationPanel from '../../components/kpiOnePager/ChartConfigurationPanel';
import OnePagerTable from '../../components/kpiOnePager/OnePagerTable';
import OnePagerMobileView from '../../components/kpiOnePager/OnePagerMobileView';
import { useToast } from '../../../../components/LhToastContainer';
import { useTheme } from '../../../../theme';

// ─── Feature Flag ─────────────────────────────────────────────────────────────

/** Toggle between backend API and local dummy data */
const USE_BACKEND = true;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface KPIOnePagerV2TabProps {
	/** Filters passed from the dashboard */
	filters?: OnePagerFilters;
}

// ─── Dummy Data (preserved for QA/development when USE_BACKEND = false) ──────

/** Sample locations for development/demo purposes */
const DUMMY_LOCATIONS = [
	{ rg: 'Atlantic', mk: 'DMV', lc: 'Auburn WA', lt: 'Warehouse' },
	{ rg: 'Atlantic', mk: 'DMV', lc: 'Baltimore MD', lt: 'Warehouse' },
	{ rg: 'Atlantic', mk: 'Northeast', lc: 'Boston MA', lt: 'Distribution' },
	{ rg: 'Atlantic', mk: 'Northeast', lc: 'Hartford CT', lt: 'Warehouse' },
	{ rg: 'Central', mk: 'Great Lakes', lc: 'Chicago IL', lt: 'Warehouse' },
	{ rg: 'Central', mk: 'Great Lakes', lc: 'Detroit MI', lt: 'Distribution' },
	{ rg: 'Central', mk: 'Heartland', lc: 'Dallas TX', lt: 'Warehouse' },
	{ rg: 'Central', mk: 'Heartland', lc: 'Houston TX', lt: 'Distribution' },
	{ rg: 'West', mk: 'Pacific', lc: 'Los Angeles CA', lt: 'Warehouse' },
	{ rg: 'West', mk: 'Pacific', lc: 'San Francisco CA', lt: 'Distribution' },
	{ rg: 'West', mk: 'Mountain', lc: 'Denver CO', lt: 'Warehouse' },
	{ rg: 'West', mk: 'Mountain', lc: 'Phoenix AZ', lt: 'Distribution' },
	{ rg: 'South', mk: 'Southeast', lc: 'Atlanta GA', lt: 'Warehouse' },
	{ rg: 'South', mk: 'Southeast', lc: 'Miami FL', lt: 'Distribution' },
	{ rg: 'South', mk: 'Gulf', lc: 'New Orleans LA', lt: 'Warehouse' },
];

/**
 * Seeded pseudo-random number generator for consistent dummy values.
 */
const seededRandom = (seed: string): number => {
	let hash = 0;
	for (let i = 0; i < seed.length; i++) {
		const char = seed.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash |= 0;
	}
	return Math.abs((Math.sin(hash) * 10000) % 1);
};

/**
 * Generate a dummy KPI value for a location + column combination.
 */
const getDummyValue = (locationKey: string, columnId: string): number => {
	const col = PAGER_COLUMNS.find((c) => c.id === columnId);
	if (!col) return 0;
	const rand = seededRandom(`${locationKey}-${columnId}`);
	const min = Math.min(col.best, col.worst);
	const max = Math.max(col.best, col.worst);
	return min + rand * (max - min);
};

// ─── Default Filters ──────────────────────────────────────────────────────────

const DEFAULT_FILTERS: OnePagerFilters = {
	region: [],
	market: [],
	location: [],
	locationType: [],
	year: [],
	period: [],
	week: [],
};

// ─── Component ────────────────────────────────────────────────────────────────

const KPIOnePagerV2Tab: React.FC<KPIOnePagerV2TabProps> = ({ filters }) => {
	const dispatch = useDispatch();
	const toast = useToast();
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

	// ─── Mobile detection ───────────────────────────────────────────────
	const [isMobile, setIsMobile] = useState(() => {
		const { width } = Dimensions.get('window');
		return width < 768;
	});

	useEffect(() => {
		const handleDimensionsChange = () => {
			const { width } = Dimensions.get('window');
			setIsMobile(width < 768);
		};

		const subscription = Dimensions.addEventListener('change', handleDimensionsChange);
		return () => subscription?.remove();
	}, []);

	// ─── Redux selectors ────────────────────────────────────────────────
	const grainSelections = useSelector(selectGrainSelections);
	const pendingGrains = useSelector(selectPendingGrains);
	const grainSlotCount = useSelector(selectGrainSlotCount);
	const manualOverride = useSelector(selectManualOverride);
	const sort = useSelector(selectSort);
	const isConfigExpanded = useSelector(selectIsConfigExpanded);

	// ─── Effective filters (from props or default) ──────────────────────
	const effectiveFilters = useMemo(
		() => filters ?? DEFAULT_FILTERS,
		[filters]
	);

	// ─── Backend data source (useOnePager hook) ─────────────────────────
	const backendData = useOnePager({
		filters: effectiveFilters,
		chartConfiguration: { grainSelections },
		enabled: USE_BACKEND && !isMobile,
	});

	// ─── Dummy data source (local grouping/aggregation) ─────────────────
	const dummyRows: GroupedRow[] = useMemo(() => {
		if (USE_BACKEND) return []; // Skip computation when using backend
		if (grainSelections.length === 0) return [];

		const groups = new Map<string, { members: typeof DUMMY_LOCATIONS; grainVals: Record<string, string> }>();

		DUMMY_LOCATIONS.forEach((loc) => {
			const parts: string[] = grainSelections.map((key) => {
				switch (key) {
					case 'rg': return loc.rg;
					case 'mk': return loc.mk;
					case 'lc': return loc.lc;
					case 'lt': return loc.lt;
					case 'yr': return '2026';
					case 'pd': return 'P7';
					case 'wk': return 'W28';
					case 'dt': return '2026-07-13';
					default: return '';
				}
			});

			const compositeKey = parts.join('|||');

			if (!groups.has(compositeKey)) {
				const grainVals: Record<string, string> = {};
				grainSelections.forEach((k, i) => {
					grainVals[k] = parts[i];
				});
				groups.set(compositeKey, { members: [], grainVals });
			}

			groups.get(compositeKey)!.members.push(loc);
		});

		const result: GroupedRow[] = [];
		groups.forEach((group) => {
			const vals: Record<string, number> = {};
			PAGER_COLUMNS.forEach((col) => {
				let sum = 0;
				group.members.forEach((loc) => {
					sum += getDummyValue(loc.lc, col.id);
				});
				vals[col.id] = col.gAgg === 'sum' ? sum : sum / group.members.length;
			});
			result.push({
				grainVals: group.grainVals,
				vals,
				memberCount: group.members.length,
			});
		});

		return result;
	}, [grainSelections]);

	const dummyGrandTotal: GroupedRow | null = useMemo(() => {
		if (USE_BACKEND) return null;
		if (dummyRows.length === 0) return null;

		const vals: Record<string, number> = {};
		PAGER_COLUMNS.forEach((col) => {
			let sum = 0;
			dummyRows.forEach((row) => {
				sum += row.vals[col.id] ?? 0;
			});
			vals[col.id] = col.gAgg === 'sum' ? sum : sum / dummyRows.length;
		});

		return {
			grainVals: {},
			vals,
			memberCount: DUMMY_LOCATIONS.length,
		};
	}, [dummyRows]);

	// ─── Resolve data source based on feature flag ──────────────────────
	const rows = USE_BACKEND ? backendData.rows : dummyRows;
	const grandTotalRow = USE_BACKEND ? backendData.grandTotalRow : dummyGrandTotal;
	const loading = USE_BACKEND ? backendData.loading : false;
	const totalRows = USE_BACKEND ? backendData.totalRows : dummyRows.length;
	const totalPages = USE_BACKEND ? backendData.totalPages : 1;
	const page = USE_BACKEND ? backendData.page : 1;
	const pageSize = USE_BACKEND ? backendData.size : 25;
	const { isExporting, exportOnePager } = useOnePagerExport({ defaultPageSize: 250 });

	// ─── Sorted rows (client-side sort only for dummy data) ─────────────
	// When using backend, sorting is server-side (handled by useOnePager)
	const sortedRows = useMemo(
		() => USE_BACKEND ? rows : sortGrainRows(rows, sort, grainSelections),
		[rows, sort, grainSelections],
	);

	// ─── Callbacks ──────────────────────────────────────────────────────
	const handleToggleExpand = useCallback(() => {
		dispatch(toggleConfigPanel());
	}, [dispatch]);

	const handleGrainChange = useCallback((index: number, value: string) => {
		dispatch(setPendingGrain({ index, value }));
	}, [dispatch]);

	const handleAddSlot = useCallback(() => {
		dispatch(addGrainSlot());
	}, [dispatch]);

	const handleRemoveSlot = useCallback((index: number) => {
		dispatch(removeGrainSlot(index));
	}, [dispatch]);

	const handleApply = useCallback(() => {
		dispatch(applyGrainConfig());
	}, [dispatch]);

	const handleReset = useCallback(() => {
		dispatch(resetGrainConfig());
		dispatch(setAutoDrillGrains({
			selections: [...DEFAULT_GRAIN_SELECTIONS],
			slotCount: DEFAULT_GRAIN_SELECTIONS.length,
		}));
		toast.show('Chart Configuration reset to auto drill-down.', 'success');
	}, [dispatch, toast]);

	const handleSort = useCallback((col: string) => {
		dispatch(setSort(col));
	}, [dispatch]);

	const handleNavigateToOverview = useCallback((_filterType: NavigableFilterType, _value: string) => {
		// Future: navigate to KPI Overview with the selected filter applied
	}, []);

	const handleExport = useCallback(async () => {
		try {
			await exportOnePager({
				filters: effectiveFilters,
				grainSelections,
				sort,
				columns: PAGER_COLUMNS,
			});
		} catch (error: any) {
			if (error instanceof OnePagerExportError) {
				if (error.category === 'NO_DATA') {
					toast.show('No data available for export.', 'warning', 'Export');
					return;
				}
			}

			toast.show('Unable to export One Pager. Please try again.', 'error', 'Export Failed');
		}
	}, [exportOnePager, effectiveFilters, grainSelections, sort, toast]);

	const handlePageChange = useCallback((newPage: number) => {
		if (USE_BACKEND) {
			backendData.setPage(newPage);
		}
	}, [backendData]);

	const handlePageSizeChange = useCallback((newSize: number) => {
		if (USE_BACKEND) {
			backendData.setSize(newSize);
		}
	}, [backendData]);

	// ─── Render ─────────────────────────────────────────────────────────
	return (
		<View style={[styles.container, isDark && { backgroundColor: colors.background.page }]}>
			{isMobile ? (
				// Mobile View
				<OnePagerMobileView
					filters={effectiveFilters}
					columns={PAGER_COLUMNS}
				/>
			) : (
				// Desktop/Tablet View
				<ScrollView
					style={styles.scrollView}
					contentContainerStyle={styles.scrollContent}
					showsVerticalScrollIndicator={false}
				>
					{/* Chart Configuration Panel (Grain Selection) */}
					<ChartConfigurationPanel
						isExpanded={isConfigExpanded}
						pendingGrains={pendingGrains}
						grainSlotCount={grainSlotCount}
						grainSelections={grainSelections}
						manualOverride={manualOverride}
						onToggleExpand={handleToggleExpand}
						onGrainChange={handleGrainChange}
						onAddSlot={handleAddSlot}
						onRemoveSlot={handleRemoveSlot}
						onApply={handleApply}
						onReset={handleReset}
					/>

					{/* One Pager Table */}
					<OnePagerTable
						rows={sortedRows}
						grandTotalRow={grandTotalRow}
						grainSelections={grainSelections}
						columns={PAGER_COLUMNS}
						sort={sort}
						loading={loading}
						onSort={handleSort}
						onNavigateToOverview={handleNavigateToOverview}
						onExport={handleExport}
						isExporting={isExporting}
						totalRows={totalRows}
						totalPages={totalPages}
						page={page}
						pageSize={pageSize}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
					/>
				</ScrollView>
			)}
		</View>
	);
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: '#F8F9FA',
	},
	scrollView: {
		flex: 1,
	},
	scrollContent: {
		padding: 0,
		gap: 0,
	},
});

export default KPIOnePagerV2Tab;
